import React, { useState, useRef, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b'];

const UserGrowthChart = ({ data }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const setHoverRef = useRef(setHoverIndex);
  useEffect(() => { setHoverRef.current = setHoverIndex; }, []);

  const chartEvents = useRef({
    dataPointMouseEnter: function(event, chartContext, config) {
      const idx = config && config.dataPointIndex;
      if (typeof idx === 'number' && idx >= 0) setTimeout(() => setHoverRef.current(idx), 0);
    },
    dataPointMouseLeave: function() {
      setTimeout(() => setHoverRef.current(null), 0);
    },
    dataPointSelection: function(event, chartContext, config) {
      const idx = config && config.dataPointIndex;
      if (typeof idx === 'number' && idx >= 0) setTimeout(() => setHoverRef.current(idx), 0);
    },
    mouseLeave: function() {
      setTimeout(() => setHoverRef.current(null), 0);
    }
  }).current;

  const options = {
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
      events: chartEvents
    },
      colors: ['#10b981', '#3b82f6', '#f59e0b'],
    stroke: {
      curve: 'smooth',
      width: 3
    },
    markers: {
      size: 8,
      hover: { size: 10 }
    },
    xaxis: {
      categories: data?.dates || [],
      title: {
        text: 'Date'
      }
    },
    yaxis: {
      title: {
        text: 'Number of Users'
      }
    },
    title: {
      text: 'User Growth Trends',
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right'
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      custom: function({ dataPointIndex, w }) {
        if (dataPointIndex != null && w?.globals?.labels) {
          setTimeout(() => setHoverRef.current(dataPointIndex), 0);
        }
        return '';
      }
    },
    grid: {
      borderColor: '#f1f1f1'
    }
  };

  const series = [
    {
      name: 'New Users',
      data: data?.signups || []
    },
    {
      name: 'Deleted Users',
      data: data?.activeUsers || []
    },
    {
      name: 'Total Users',
      data: data?.totalUsers || []
    }
  ];

  const dates = data?.dates || [];
  const signups = data?.signups || [];
  const activeUsers = data?.activeUsers || [];
  const totalUsersArr = data?.totalUsers || [];

  const showCard = hoverIndex != null && dates[hoverIndex] != null;
  const cardDate = showCard ? dates[hoverIndex] : '';
  const cardNew = showCard ? (signups[hoverIndex] ?? 0) : 0;
  const cardDeleted = showCard ? (activeUsers[hoverIndex] ?? 0) : 0;
  const cardTotal = showCard ? (totalUsersArr[hoverIndex] ?? 0) : 0;
  const totalForPct = cardTotal || (cardNew + cardDeleted) || 1;

  return (
    <div
      className="chart-container"
      style={{ position: 'relative' }}
      onMouseLeave={() => setHoverIndex(null)}
    >
      {showCard && (
        <div
          style={{
            position: 'absolute', top: 16, right: 16, zIndex: 10,
            padding: 16, background: '#fff', borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', borderLeft: '4px solid #3b82f6',
            minWidth: 220, pointerEvents: 'none'
          }}
        >
          <div style={{ fontWeight: 700, color: '#242424', marginBottom: 12, fontSize: 16 }}>{cardDate}</div>
          {[
            { label: 'New Users', value: cardNew, color: COLORS[0] },
            { label: 'Deleted Users', value: cardDeleted, color: COLORS[1] },
            { label: 'Total Users', value: cardTotal, color: COLORS[2] }
          ].map((item) => {
            const pct = totalForPct > 0 ? ((item.value / totalForPct) * 100).toFixed(1) : 0;
            return (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 12, height: 12, background: item.color, borderRadius: 3 }} />
                  <span style={{ color: '#666', fontSize: 13 }}>{item.label}:</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#242424', fontWeight: 600, fontSize: 13 }}>{(item.value || 0).toLocaleString()}</div>
                  <div style={{ color: '#999', fontSize: 11 }}>{pct}%</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ReactApexChart options={options} series={series} type="line" height={350} />
    </div>
  );
};

export default UserGrowthChart;
